import { FormGroup, InputGroup } from '@blueprintjs/core';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { Box } from 'react-layout-components';
import { t } from 'ttag';

import { IMPORT_EXTENSIONS_AND_MIMETYPES } from 'uf/import/mimetypes';
import FileUpload from 'uf/ui/base/FileUpload/FileUpload';
import MessageArea from 'uf/ui/base/MessageArea/MessageArea';
import { supportedBoundariesHint } from 'uf/ui/importer/text';

import styles from './UploadBoundary.module.css';
import { useLoadTemporaryShapesMutation } from 'uf-api-rtk/store/Api';
import { convertRequestBodyToFormData } from 'uf/data/helpers';

interface OwnProps {
  disabled?: boolean;
  filenameControlLabel?: string;
  onUpload?: (fileUploadKey: string) => void;
  onNameChange?: (newName: string) => void;
}

const uploadClassName = 'CreateProjectForm-projectArea-dropzone';

export default function UploadBoundary({
  disabled,
  filenameControlLabel,
  onUpload,
  onNameChange,
}: OwnProps) {
  const [filename, setFilename] = useState('');
  const [loadTemporaryShape, { data, error, isLoading, isSuccess }] =
    useLoadTemporaryShapesMutation();
  useEffect(() => {
    if (isSuccess && data) {
      onUpload(data);
    }
  }, [isSuccess, data, onUpload]);
  const handleDrop = (file: File, nameSuggestion: string) => {
    if (!file) {
      return;
    }
    handleNameChange(nameSuggestion);
    loadTemporaryShape({
      // @ts-ignore
      body: convertRequestBodyToFormData({ formData: file }),
    });
  };
  const handleNameChange = newName => {
    setFilename(newName);
    onNameChange(newName);
  };
  const fileClassName = classNames({
    [styles.dropzoneWithFile]: !!(isSuccess && !isLoading && !error),
    [styles.dropzoneWithError]: !!error,
  });
  return (
    <Box column>
      <Box alignItems="center">
        <FileUpload
          accept={IMPORT_EXTENSIONS_AND_MIMETYPES}
          disabled={disabled}
          small
          className={uploadClassName}
          emptyMessage={
            <Box fit center className="h6">
              {t`Drop your GeoJSON, GeoPackage, or zipped shapefile here, or click to upload.`}
            </Box>
          }
          errorMessage={
            <Box fit center className="h6">
              {getErrorMessage(error)}
            </Box>
          }
          fileClassName={fileClassName}
          onDrop={handleDrop}
          hasError={!!error}
        />
        {isSuccess && (
          <FormGroup
            label={filenameControlLabel}
            className={styles.filenameField}>
            <InputGroup
              type="text"
              value={filename}
              onChange={e => handleNameChange(e.target.value)}
            />
          </FormGroup>
        )}
      </Box>
      <MessageArea className={styles.messageArea}>
        {supportedBoundariesHint}
      </MessageArea>
    </Box>
  );
}

function getErrorMessage(error): string {
  if (!error) {
    return null;
  }
  switch (error.data.type) {
    case 'too_many_temp_geos':
      return t`Upload failed. Please try a file with fewer geometries.`;
    case 'invalid_project_area_upload':
      return t`Upload failed. Project area boundary must be a polygon or multipolygon.`;
    default:
      if (error.data.detail) {
        return error.data.detail;
      }
      return t`Something unexpected occurred. Please try again.`;
  }
}
